import React, { useEffect, useState } from 'react'
import { Card, CardBody, Col, Row, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Table, Badge } from 'reactstrap';
import Chart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";
// The four KPI icon PNGs that used to be imported here are gone — StatCard
// renders remixicon glyphs instead, which follow the brand tokens and drop four
// image requests. The files remain in assets/images/ pending a wider asset audit.
import { getDashboardData } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import Image from '../../components/Image'
import dummyRoundedImage from "../../assets/images/dummy-rounded-image.png"
import TrialAlert from '../../components/TrialAlert';
import { useAppContext } from '../../context/AppContext';
import { barChartOptions } from '../../helper/chart-theme';
import StatCard from '../../components/StatCard';
import { EmptyRow } from '../../components/EmptyState';

const Dashboard = () => {
  const { isTrial, fetchUserOnboardingStatus } = useAppContext();
  const [dropdownOpenWeeks, setDropdownOpenWeeks] = useState(false);
  const [dropdownOpenCampaign, setDropdownOpenCampaign] = useState(false);
  const [dropdownOpenAgent, setDropdownOpenAgent] = useState(false);
  const [selectedWeeks, setSelectedWeeks] = useState(8);
  const [selectedCampaign, setSelectedCampaign] = useState(5);
  const [selectedAgent, setSelectedAgent] = useState(5);
  const [stats, setStats] = useState({ callsLast7Days: {}, convertedCallsLast7Days: {}, conversionRateLast7Days: {} });
  const [graphData, setGraphData] = useState({ series: [], options: {} });
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleWeeks = () => setDropdownOpenWeeks(prevState => !prevState);
  const toggleCampaign = () => setDropdownOpenCampaign(prevState => !prevState);
  const toggleAgent = () => setDropdownOpenAgent(prevState => !prevState);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedWeeks, selectedAgent, selectedCampaign])

  useEffect(() => {
    const currentUserId = localStorage.getItem("userId");
    fetchUserOnboardingStatus(currentUserId)
  }, [])

  const fetchDashboardData = () => {
    setLoading(true)
    setCampaigns([])
    getDashboardData({ weeks: selectedWeeks, topAgents: selectedAgent, topCampaigns: selectedCampaign }).then(({ data }) => {
      console.log("dashboard", data)
      if (data) {
        setStats(data.stats);
        const categories = data.weeklyStatus.map(item => item.week);
        const callsMade = data.weeklyStatus.map(item => item.totalCalls);
        const callsConverted = data.weeklyStatus.map(item => item.convertedCalls);
        setGraphData(
          {
            series: [
              {
                name: "Calls Made",
                data: callsMade,
              },
              {
                name: "Calls Converted",
                data: callsConverted,
              },
            ],
            // Palette, grid, legend and tooltip now come from the shared brand
            // theme; only what is genuinely chart-specific stays here. This also
            // drops the old `endingShape: "rounded"`, which ApexCharts removed in
            // v3.24 — the installed version is 4.4.0, so it never did anything.
            options: barChartOptions({
              chart: { height: 350 },
              xaxis: { categories: categories },
              tooltip: { y: { formatter: (val) => val?.toLocaleString() } },
            }),
          }
        );
        setAgents(data.bestPerformingAgents);
        setCampaigns(data.bestPerformingCampaigns);
      }
      else {
        showToast("error", "Request Failed", data.message)
      }
      setLoading(false)
    }).catch((err) => {
      showToast("error", "Request Failed", err.message)
    })
  }

  // const chartData = {

  // };
  return (
    <div className="content-page">
      <div className="content">

        <div className="container-fluid">
          <div className="py-3 row">
            <div className="col-12">
              <Row className="my-2 align-items-center">
                <Col md={12}>
                  <h4>Dashboard</h4>
                </Col>
                {/* Cards */}
              </Row>
            </div>
          </div>
          {
            isTrial ? <TrialAlert /> : null
          }
          <Row>
            {/* Four KPI tiles. Each was ~26 lines of near-identical markup with a
                raster PNG icon; StatCard collapses them and swaps the images for
                remixicon glyphs that follow the brand tokens. */}
            <Col md={3}>
              {loading ? <Skeleton height={158} borderRadius={16} /> : (
                <StatCard
                  icon="ri-timer-line"
                  tone="brand"
                  label="Used Minutes this Month"
                  value={stats && Math.round(stats.totalMinutesThisMonth / 60).toLocaleString()}
                  unit="mins"
                />
              )}
            </Col>
            <Col md={3}>
              {loading ? <Skeleton height={158} borderRadius={16} /> : (
                <StatCard
                  icon="ri-phone-line"
                  tone="brand"
                  label="Total Calls Made"
                  value={stats?.callsLast7Days?.current}
                  change={stats?.callsLast7Days?.change}
                  caption="Last 7 days"
                />
              )}
            </Col>
            <Col md={3}>
              {loading ? <Skeleton height={158} borderRadius={16} /> : (
                <StatCard
                  icon="ri-checkbox-circle-line"
                  tone="brand"
                  label="Total Calls Converted"
                  value={stats?.convertedCallsLast7Days?.current}
                  change={stats?.convertedCallsLast7Days?.change}
                  caption="Last 7 days"
                />
              )}
            </Col>
            <Col md={3}>
              {loading ? <Skeleton height={158} borderRadius={16} /> : (
                <StatCard
                  icon="ri-line-chart-line"
                  tone="violet"
                  label="Conversion Rate"
                  value={
                    stats?.conversionRateLast7Days?.currentRate !== undefined
                      ? `${stats.conversionRateLast7Days.currentRate.toFixed(1)}%`
                      : null
                  }
                  change={stats?.conversionRateLast7Days?.change}
                  caption="Last 7 days"
                />
              )}
            </Col>
          </Row>
          {/* Graph */}
          <Row>
            <Col md={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Weekly Status</h5>
                    <Dropdown isOpen={dropdownOpenWeeks} toggle={toggleWeeks}>
                      <DropdownToggle caret color="light" size="sm">
                        Last {selectedWeeks} weeks <i className="ri-arrow-down-s-line"></i>
                      </DropdownToggle>
                      <DropdownMenu>
                        {[4, 8, 12].map((week) => (
                          <DropdownItem key={week} onClick={() => setSelectedWeeks(week)}>
                            Last {week} weeks
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  {
                    loading ?
                      <Skeleton height={350} />
                      :
                      <Chart options={graphData.options} series={graphData.series} type="bar" height={350} />
                  }
                </CardBody>
              </Card>
            </Col>
          </Row>
          {/* Tables */}
          <Row>
            <Col md={6}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Best Performing Campaigns</h5>
                    <Dropdown isOpen={dropdownOpenCampaign} toggle={toggleCampaign}>
                      <DropdownToggle caret color="light" size="sm">
                        Top {selectedCampaign} <i className="ri-arrow-down-s-line"></i>
                      </DropdownToggle>
                      <DropdownMenu>
                        {[5, 10].map((campaign) => (
                          <DropdownItem key={campaign} onClick={() => setSelectedCampaign(campaign)}>
                            Top {campaign}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  <Table hover responsive className='border-rounded'>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th className="text-center">Calls Made</th>
                        <th className="text-center">Calls Converted</th>
                        <th className="text-center">Conversion Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        loading ?
                          [...Array(5)].map((a, i) => {
                            return <tr key={i}>
                              <td className="fs-12"><Skeleton height={12} width={80} /></td>
                              <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                              <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                              <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                            </tr>
                          }) :
                          campaigns.length === 0 ?
                            <EmptyRow colSpan={4} icon="ri-megaphone-line" title="No campaigns yet" body="Campaign performance appears here once you launch one." /> :
                            campaigns.map((campaign, i) => {
                              return <tr key={i}>

                                <td className="fs-12 fw-500 w-30">{campaign.campaignName}</td>
                                <td className="fs-12 fw-500 text-center">{campaign.totalCalls}</td>
                                <td className="fs-12 fw-500 text-center">{campaign.convertedCalls}</td>
                                <td className="fs-12 fw-500 text-center"><Badge color='badge-info badge-soft-info fs-14'>{campaign?.conversionRate && campaign.conversionRate.toFixed(1)}%</Badge></td>
                              </tr>
                            })
                      }
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Best Performing Agents</h5>
                    <Dropdown isOpen={dropdownOpenAgent} toggle={toggleAgent}>
                      <DropdownToggle caret color="light" size="sm">
                        Top {selectedAgent} <i className="ri-arrow-down-s-line"></i>
                      </DropdownToggle>
                      <DropdownMenu>
                        {[5, 10].map((agent) => (
                          <DropdownItem key={agent} onClick={() => setSelectedAgent(agent)}>
                            Top {agent}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  <Table hover responsive className='border-rounded'>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th className="text-center">Calls Made</th>
                        <th className="text-center">Calls Converted</th>
                        <th className="text-center">Conversion Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        loading ?
                          [...Array(5)].map((a, i) => {
                            return <tr key={i}>
                              <td className="fs-12"><div className='d-flex align-items-center'><Skeleton height={40} width={40} circle={true} className='me-2' /><Skeleton height={12} width={90} /></div></td>
                              <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                              <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                              <td className="fs-12 text-center"><Skeleton height={12} width={80} /></td>
                            </tr>
                          }) :
                          agents.length === 0 ?
                            <EmptyRow colSpan={4} icon="ri-user-voice-line" title="No agents yet" body="Agent performance appears here once your agents start taking calls." /> :
                            agents.map((agent, i) => {
                              return <tr key={i}>
                                <td className="fs-12 fw-500 w-30"><div className='d-flex align-items-center'><Image src={dummyRoundedImage} alt="Event" className='rounded-circle me-2' height='40px' width='40px' objectFit='cover' /><p className='m-0'>{agent.agentName}</p></div></td>
                                <td className="fs-12 fw-500 text-center">{agent.totalCalls}</td>
                                <td className="fs-12 fw-500 text-center">{agent.convertedCalls}</td>
                                <td className="fs-12 fw-400 text-center"><Badge color='badge-info badge-soft-info fs-14'>{agent?.conversionRate && agent.conversionRate.toFixed(1)}%</Badge></td>
                              </tr>
                            })
                      }
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
